import { computed, inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { select } from '@ngxs/store';
import { Observable } from 'rxjs';
import { filter, first, map, switchMap, take } from 'rxjs/operators';

import { AppSelectors, GenericSelectors } from '@store/selectors';
import { FeatureflagsSelectors } from '@store/selectors/featureflags.selectors';
import { SettingsSelectors } from '@store/selectors/settings.selectors';
import { AppVariants } from '@store/models';
import { Settings } from '@models/settings';

@Injectable({ providedIn: 'root' })
export class AccessService {
  private readonly store = inject(Store);

  private readonly userInfo = select(AppSelectors.userInfo);
  private readonly featureFlags = select(FeatureflagsSelectors.featureFlags);
  private readonly featureFlagsLoaded = select(FeatureflagsSelectors.loaded);
  private readonly appVariant = select(GenericSelectors.AppVariant);

  /** True once the data the guards rely on (user roles + feature flags) is in the store. */
  private readonly ready = computed(() => !!this.userInfo() && this.featureFlagsLoaded());

  // Bridged to an Observable here (field initializer = injection context) because guards
  // must return an async primitive, and toObservable() cannot be created lazily mid-navigation.
  private readonly ready$ = toObservable(this.ready);

  private readonly featureAccess: Record<string, () => boolean> = {
    voorbereidingstoets: () =>
      this.appVariant() === AppVariants.DOE &&
      !!this.featureFlags()['voorbereidingstoets'],
    schrijfvaardigheid: () =>
      this.appVariant() === AppVariants.DOE &&
      !!this.featureFlags()['schrijfvaardigheid'],
    downloads: () => this.appVariant() === AppVariants.OCW,
  };

  /**
   * Emits once the store holds the data the route guards rely on (user roles and
   * feature flags). On a hard reload these are dispatched asynchronously after auth,
   * so guards must wait for this "sign of life" before evaluating access — otherwise
   * they read the initial (empty) state and wrongly deny.
   */
  whenReady(): Observable<true> {
    return this.ready$.pipe(
      first(Boolean),
      map(() => true as const),
    );
  }

  canAccessFeature(featureKey: string): boolean {
    const rule = this.featureAccess[featureKey];
    if (!rule) {
      return true;
    }
    return rule();
  }

  hasAnyRole(requiredRoles: string[]): boolean {
    if (!requiredRoles || requiredRoles.length === 0) {
      return true;
    }
    const roles = this.userInfo()?.roles || [];
    return requiredRoles.some((required) =>
      roles.some((userRole) => required.toLowerCase() === userRole.toLowerCase()),
    );
  }

  checkAvailability(availabilityKey: string): Observable<boolean> {
    switch (availabilityKey) {
      case 'rapportages':
        return this.checkDateAvailability('rapportagesBeschikbaarVanaf');
      case 'downloads':
        return this.checkDateAvailability('afnamesBeschikbaarVanaf');
      default:
        return new Observable((subscriber) => {
          subscriber.next(true);
          subscriber.complete();
        });
    }
  }

  private checkDateAvailability(settingsKey: keyof Settings): Observable<boolean> {
    return this.store.select(SettingsSelectors.isSettingsLoaded).pipe(
      filter(Boolean),
      take(1),
      switchMap(() => this.store.select(SettingsSelectors.settings).pipe(take(1))),
      map((settings) => {
        const dateValue = settings?.[settingsKey];
        return dateValue ? new Date() >= new Date(dateValue as string) : false;
      }),
    );
  }
}
